<template>
    <div>
        <div class="templates-container animated fadeIn">
            <b-card class="templates-card">
                <div class="templates-header mb-4">
                    <div>
                        <h4>{{ $t('templateslabel') }}</h4>
                        <p class="text-muted mb-0">{{ $t('manageYourTemplates') }}</p>
                    </div>
                    <b-button variant="primary" size="lg" @click="goToMarketplace">
                        <b-icon icon="plus-circle"></b-icon> {{ $t('addMore') }}
                    </b-button>
                </div>

                <!-- Tabs -->
                <b-tabs v-model="activeTab" content-class="mt-3">
                    <b-tab :title="$t('marketplaceTemplates')" active>
                        <!-- Loading State -->
                        <div v-if="loading" class="loading-state">
                            <div class="templates-grid">
                                <div v-for="i in 3" :key="i" class="template-card-skeleton">
                                    <b-skeleton animation="throb" height="80px" class="mb-3"></b-skeleton>
                                    <b-skeleton animation="throb" width="60%" height="20px" class="mb-2"></b-skeleton>
                                    <b-skeleton animation="throb" width="40%" height="16px"></b-skeleton>
                                </div>
                            </div>
                        </div>

                        <!-- Empty State -->
                        <div v-else-if="ownedTemplates.length === 0" class="empty-state">
                            <div class="empty-icon">
                                <b-icon icon="file-earmark-text" font-scale="4"></b-icon>
                            </div>
                            <h3 class="empty-title">{{ $t('noTemplatesYet') }}</h3>
                            <p class="empty-subtitle">{{ $t('noTemplatesDescription') }}</p>
                            <b-button variant="primary" size="lg" @click="goToMarketplace">
                                <b-icon icon="shop"></b-icon> {{ $t('exploreMarketplace') }}
                            </b-button>
                        </div>

                        <!-- Templates Grid -->
                        <div v-else class="templates-grid">
                            <div 
                                v-for="template in ownedTemplates" 
                                :key="template.id"
                                class="template-card"
                                :class="{ 'installed': isInstalled(template), 'selected': selectedTemplate && selectedTemplate.id === template.id }"
                                @click="selectTemplate(template)"
                            >
                                <div class="template-image" v-if="template.cover">
                                    <img :src="template.cover" :alt="$translateLabel(template.label || template.name)" />
                                </div>
                                <div class="template-image template-placeholder" v-else>
                                    <b-icon icon="file-earmark-text" font-scale="3"></b-icon>
                                </div>
                                
                                <div class="template-content">
                                    <div class="template-header">
                                        <h5 class="template-name">{{ $translateLabel(template.label || template.name) }}</h5>
                                        <b-badge 
                                            :variant="isInstalled(template) ? 'success' : template.isOwnedNotInstalled ? 'warning' : 'secondary'" 
                                            pill
                                            class="status-badge"
                                        >
                                            <b-icon :icon="isInstalled(template) ? 'check-circle-fill' : template.isOwnedNotInstalled ? 'cart-check-fill' : 'circle'"></b-icon>
                                            {{ isInstalled(template) ? $t('installed') : template.isOwnedNotInstalled ? $t('owned') : $t('notInstalled') }}
                                        </b-badge>
                                    </div>
                                    
                                    <p class="template-description" v-if="template.description">
                                        {{ $translateLabel(template.description) }}
                                    </p>
                                    
                                    <div class="template-actions">
                                        <!-- Installed templates: show Uninstall -->
                                        <b-button 
                                            variant="outline-danger" 
                                            size="sm" 
                                            @click.stop="uninstallTemplate(template)"
                                            v-if="isInstalled(template)"
                                        >
                                            <b-icon icon="trash"></b-icon> {{ $t('uninstall') }}
                                        </b-button>
                                        
                                        <!-- Owned but not installed: show Install button -->
                                        <b-button 
                                            variant="success" 
                                            size="sm" 
                                            @click.stop="installTemplate(template)"
                                            v-else-if="template.isOwnedNotInstalled"
                                        >
                                            <b-icon icon="download"></b-icon> {{ $t('install') }}
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-tab>

                    <b-tab :title="$t('myTemplates')">
                        <!-- Coming Soon -->
                        <div class="coming-soon-state">
                            <div class="coming-soon-icon">
                                <b-icon icon="hourglass-split" font-scale="4"></b-icon>
                            </div>
                            <h3 class="coming-soon-title">{{ $t('comingSoon') }}</h3>
                            <p class="coming-soon-subtitle">{{ $t('myTemplatesComingSoon') }}</p>
                        </div>
                    </b-tab>
                </b-tabs>
            </b-card>
        </div>

        <!-- Template Details Modal -->
        <main-dialog 
            v-model="showTemplateModal"
            :title="selectedTemplate ? $translateLabel(selectedTemplate.label || selectedTemplate.name) : ''"
            size="xl"
            centered
            scrollable
            hide-footer
        >
            <div v-if="selectedTemplate">
                <template v-if="loadingDetails">
                    <b-skeleton width="100%" height="500px" animation="throb" />
                </template>
                <template v-else-if="selectedTemplateDetails">
                    <div class="template-modal-header mb-4" v-if="selectedTemplate.cover">
                        <img :src="selectedTemplate.cover" class="template-modal-cover" />
                    </div>
                    <TemplateDescription :item="selectedTemplateModel" type="single" />
                    
                    <div class="template-modal-actions mt-4 pt-3 border-top">
                        <b-button 
                            variant="success" 
                            @click="installTemplate(selectedTemplate)"
                            v-if="selectedTemplate.isOwnedNotInstalled"
                        >
                            <b-icon icon="download"></b-icon> {{ $t('install') }}
                        </b-button>
                        <b-button 
                            variant="danger" 
                            @click="uninstallTemplate(selectedTemplate)"
                            v-else-if="isInstalled(selectedTemplate)"
                        >
                            <b-icon icon="trash"></b-icon> {{ $t('uninstall') }}
                        </b-button>
                    </div>
                </template>
            </div>
        </main-dialog>
        
        <!-- Confirmation Modal -->
        <ConfirmModal
            :show="confirmModal.show"
            :title="confirmModal.title"
            :message="confirmModal.message"
            :confirmText="confirmModal.confirmText"
            :dangerButton="confirmModal.danger"
            :icon="confirmModal.icon"
            @confirm="handleConfirmAction"
            @cancel="closeConfirmModal"
        />
    </div>
</template>

<script>
import TemplateDescription from '../../templates/Description'
import Template from '../../../lib/Template'
import ConfirmModal from '@/components/workflow/ConfirmModal.vue'

export default {
    name: 'TemplatesList',
    
    components: {
        TemplateDescription,
        ConfirmModal
    },
    
    data() {
        return {
            loading: true,
            availableTemplates: [],
            installedTemplates: [],
            selectedTemplate: null,
            selectedTemplateDetails: null,
            selectedTemplateModel: null,
            showTemplateModal: false,
            loadingDetails: false,
            activeTab: 0,
            confirmModal: {
                show: false,
                title: '',
                message: '',
                confirmText: 'Confirm',
                danger: false,
                icon: 'warning',
                action: null,
                params: null
            }
        }
    },
    
    computed: {
        ownedTemplates() {
            // Get subscriptions to find owned templates
            const subscriptions = this.$store.getters.getSubscriptions || []
            
            // Get all template product IDs from subscriptions
            const subscribedProductIds = new Set(
                subscriptions
                    .map(sub => sub.product_reference || sub.product_id || sub.reference)
                    .filter(Boolean)
            )
            
            // Get all template IDs that are already installed
            const installedIds = new Set(
                this.installedTemplates.map(t => t.template.id)
            )
            
            const allTemplates = []
            const seenIds = new Set()
            
            // First, add all already installed templates
            this.installedTemplates.forEach(installedTemplate => {
                const templateId = installedTemplate.template.id
                if (!seenIds.has(templateId)) {
                    seenIds.add(templateId)
                    // Find the full template data from available templates
                    const fullTemplate = this.availableTemplates.find(t => t.id == templateId)
                    if (fullTemplate) {
                        allTemplates.push({
                            ...fullTemplate,
                            isInstalled: true
                        })
                    }
                }
            })
            
            // Then, add subscribed templates that are NOT yet installed
            this.availableTemplates.forEach(template => {
                const hasSubscription = subscribedProductIds.has(template.id) || 
                                       subscribedProductIds.has(template.product_id)
                const alreadyInstalled = seenIds.has(template.id)
                
                if (!seenIds.has(template.id) && hasSubscription) {
                    seenIds.add(template.id)
                    allTemplates.push({
                        ...template,
                        isOwnedNotInstalled: true
                    })
                }
            })
            
            return allTemplates
        }
    },
    
    methods: {
        isInstalled(template) {
            return this.installedTemplates.some(t => t.template.id == template.id)
        },
        
        selectTemplate(template) {
            this.selectedTemplate = template
            this.showTemplateModal = true
            this.loadTemplateDetails(template)
        },
        
        loadTemplateDetails(template) {
            this.loadingDetails = true
            this.selectedTemplateDetails = null
            
            this.$http.get(`/v1/project/template/${template.id}`).then(resp => {
                this.selectedTemplateDetails = resp.data
                this.selectedTemplateModel = new Template(this.selectedTemplateDetails, this.$store.getters.getLocale)
            }).catch(() => {
                // Error handled
            }).finally(() => {
                this.loadingDetails = false
            })
        },
        
        goToMarketplace() {
            this.$router.push({
                path: '/admin/marketplace',
                query: { category: 'templates' }
            })
        },
        
        installTemplate(template) {
            this.confirmModal = {
                show: true,
                title: this.$t('install'),
                message: this.$t('marketplace.confirmInstall'),
                confirmText: this.$t('install'),
                danger: false,
                icon: 'question',
                action: 'installTemplate',
                params: template
            }
        },
        
        async executeInstallTemplate(template) {
            const popup = this.$loadingPopup(this.$t('marketplace.installingTemplate'))
            
            try {
                // Load template details first
                const { data: templateDetails } = await this.$http.get(`/v1/project/template/${template.id}`)
                
                await this.$http.post("/v1/project/template/install", {
                    id: template.id,
                    data: {
                        components: templateDetails.components,
                        integrations: templateDetails.integrations,
                        apps: templateDetails.apps,
                        functions: templateDetails.functions
                    }
                })
                
                this.$store.commit('RELOAD_CONTAINER')
                await this.loadData()
                
                this.$bvToast.toast(this.$t('marketplace.templateInstalled'), {
                    variant: 'success',
                    title: this.$t('success')
                })
            } catch (err) {
                console.error('Template installation error:', err)
                this.$bvToast.toast(
                    this.$t('marketplace.failedToInstall'), 
                    {
                        variant: 'danger',
                        title: this.$t('error')
                    }
                )
            } finally {
                popup.hide()
            }
        },
        
        uninstallTemplate(template) {
            this.confirmModal = {
                show: true,
                title: this.$t('marketplace.uninstall'),
                message: this.$t('marketplace.confirmUninstall'),
                confirmText: this.$t('marketplace.uninstall'),
                danger: true,
                icon: 'danger',
                action: 'uninstallTemplate',
                params: template
            }
        },
        
        handleConfirmAction() {
            if (this.confirmModal.action === 'installTemplate') {
                this.executeInstallTemplate(this.confirmModal.params)
            } else if (this.confirmModal.action === 'uninstallTemplate') {
                this.executeUninstallTemplate(this.confirmModal.params)
            }
            this.closeConfirmModal()
        },
        
        closeConfirmModal() {
            this.confirmModal = {
                show: false,
                title: '',
                message: '',
                confirmText: 'Confirm',
                danger: false,
                icon: 'warning',
                action: null,
                params: null
            }
        },
        
        async executeUninstallTemplate(template) {
            const popup = this.$loadingPopup(this.$t('marketplace.uninstalling'))
            
            try {
                await this.$http.post("/v1/project/template/uninstall", { id: template.id })
                
                this.$store.commit('RELOAD_CONTAINER')
                await this.loadData()
                
                this.$bvToast.toast(this.$t('marketplace.templateUninstalled'), {
                    variant: 'success',
                    title: this.$t('success')
                })
            } catch (err) {
                console.error('Template uninstall error:', err)
                this.$bvToast.toast(this.$t('marketplace.failedToUninstall'), {
                    variant: 'danger',
                    title: this.$t('error')
                })
            } finally {
                popup.hide()
            }
        },
        
        async loadAvailableTemplates() {
            try {
                const [exploreResp, availableResp] = await Promise.all([
                    this.$http.post("/v1/project/template/search", { keyword: "", type: 'name' }),
                    this.$http.get("/v1/project/template/available")
                ])
                
                const exploreTemplates = exploreResp.data || []
                const availableTemplates = availableResp.data || []
                
                // Combine and deduplicate templates
                const templateMap = new Map()
                
                availableTemplates.forEach(t => {
                    templateMap.set(t.id, t)
                })
                
                exploreTemplates.forEach(t => {
                    if (!templateMap.has(t.id)) {
                        templateMap.set(t.id, t)
                    }
                })
                
                this.availableTemplates = Array.from(templateMap.values())
            } catch (err) {
                console.error('Failed to load available templates:', err)
                this.$bvToast.toast(this.$t('failedToLoadTemplates'), {
                    variant: 'danger',
                    title: this.$t('error')
                })
            }
        },
        
        async loadInstalledTemplates() {
            try {
                const { data = [] } = await this.$http.get("/v1/project/template/workspace")
                this.installedTemplates = data || []
            } catch (err) {
                console.error('Failed to load installed templates:', err)
            }
        },
        
        async loadData() {
            this.loading = true
            
            await Promise.all([
                this.$store.dispatch('fetchSubscriptions'),
                this.loadAvailableTemplates(),
                this.loadInstalledTemplates()
            ])
            
            this.loading = false
        }
    },
    
    async mounted() {
        await this.loadData()
    }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/style.scss';
@import '@/assets/scss/_custom.scss';

.templates-container {
    max-width: 1400px;
    margin: 0 auto;
}

.templates-card {
    .templates-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        
        h4 {
            color: var(--text-primary);
            font-weight: 600;
            margin: 0 0 4px 0;
        }
        
        p {
            font-size: 14px;
        }
    }
}

.loading-state,
.templates-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
    margin-top: 20px;
}

.template-card-skeleton {
    background: $dark-hard2-background;
    border: 1px solid $dark-border2;
    border-radius: 12px;
    padding: 20px;
}

.template-card {
    background: $dark-hard2-background;
    border: 1px solid $dark-border2;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.2s;
    cursor: pointer;
    
    &.installed {
        border-color: rgba(102, 126, 234, 0.5);
        background: rgba(49, 51, 56, 0.8);
    }
    
    &.selected {
        border-color: $protokol-royal-active;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        background: rgba(49, 51, 56, 0.9);
    }
    
    &:hover {
        border-color: $dark-border;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        transform: translateY(-2px);
    }
    
    .template-image {
        width: 100%;
        height: 180px;
        background: linear-gradient(135deg, $dark-hard2-background 0%, $dark-background 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        border-bottom: 1px solid $dark-border2;
        
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        
        &.template-placeholder {
            color: $dark-border;
        }
    }
    
    .template-content {
        padding: 20px;
        
        .template-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 8px;
            gap: 12px;
            
            .template-name {
                color: var(--text-primary);
                font-size: 16px;
                font-weight: 600;
                margin: 0;
                flex: 1;
            }
            
            .status-badge {
                font-size: 11px;
                font-weight: 500;
                padding: 4px 10px;
                display: inline-flex;
                align-items: center;
                gap: 4px;
                flex-shrink: 0;
            }
        }
        
        .template-description {
            font-size: 13px;
            color: var(--text-secondary);
            margin: 0 0 12px 0;
            line-height: 1.5;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
        
        .template-actions {
            display: flex;
            gap: 8px;
            
            .btn {
                font-size: 13px;
                padding: 6px 14px;
                display: inline-flex;
                align-items: center;
                gap: 6px;
            }
        }
    }
}

.empty-state {
    text-align: center;
    padding: 80px 20px;
    
    .empty-icon {
        margin-bottom: 24px;
        
        svg {
            color: var(--text-muted);
        }
    }
    
    .empty-title {
        color: var(--text-primary);
        font-size: 24px;
        font-weight: 600;
        margin-bottom: 12px;
    }
    
    .empty-subtitle {
        font-size: 16px;
        color: var(--text-secondary);
        margin-bottom: 32px;
        max-width: 500px;
        margin-left: auto;
        margin-right: auto;
    }
}

.coming-soon-state {
    text-align: center;
    padding: 80px 20px;
    
    .coming-soon-icon {
        margin-bottom: 24px;
        
        svg {
            color: rgba(108, 99, 255, 0.5);
        }
    }
    
    .coming-soon-title {
        color: var(--text-primary);
        font-size: 24px;
        font-weight: 600;
        margin-bottom: 12px;
    }
    
    .coming-soon-subtitle {
        font-size: 16px;
        color: var(--text-secondary);
        max-width: 500px;
        margin-left: auto;
        margin-right: auto;
    }
}

.template-modal-header {
    .template-modal-cover {
        width: 100%;
        height: auto;
        max-height: 400px;
        object-fit: cover;
        border-radius: 8px;
    }
}

.template-modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-start;
    
    .btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 20px;
    }
}
</style>
